const CPC_SIZE_ORDER = ['YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

function cpcSortVariants(variants) {
  return [...variants].sort((a, b) => {
    const ai = CPC_SIZE_ORDER.indexOf((a.title || '').toUpperCase());
    const bi = CPC_SIZE_ORDER.indexOf((b.title || '').toUpperCase());
    const aN = parseFloat(a.title);
    const bN = parseFloat(b.title);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    if (!isNaN(aN) && !isNaN(bN)) return aN - bN;
    return (a.title || '').localeCompare(b.title || '');
  });
}

function cpcFormatMoney(cents) {
  if (cents == null) return '';
  const dollars = (typeof cents === 'number' ? cents : parseFloat(cents)) / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)} USD`;
}

class CustomProductCard extends HTMLElement {
  connectedCallback() {
    this._selectedVariantId = null;
    this._qvSelectedVariantId = null;
    this._qvQty = 1;
    this._qvImgIndex = 0;

    this.addEventListener('click', this._onClick.bind(this));

    this._escHandler = (e) => {
      if (e.key === 'Escape') this._closeAll();
    };
    document.addEventListener('keydown', this._escHandler);

    this._initHoverImage();
  }

  _initHoverImage() {
    // Inline onmouseenter/onmouseleave on .cpc__img-link handles the swap.
    // This method keeps the hover src in sync after swatch updates.
  }

  // Called by product-color-swatches after updating images
  updateHoverSrc(newSrc) {
    const link = this.querySelector('.cpc__img-link');
    if (!link || !newSrc) return;
    link.setAttribute(
      'onmouseenter',
      `var p=this.querySelector('img');if(p){p._s=p.src;p.src='${newSrc}'}`
    );
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._escHandler);
  }

  // ── Event delegation ──────────────────────────────────────────────────────
  _onClick(e) {
    const t = e.target;

    // Variant button (card hover)
    const vBtn = t.closest('.cpc__variant-btn:not([disabled])');
    if (vBtn) {
      e.preventDefault();
      this._selectVariant(vBtn);
      return;
    }

    // Add to cart (card)
    const atcBtn = t.closest('[data-cpc-atc]');
    if (atcBtn && !atcBtn.disabled) {
      e.preventDefault();
      this._addToCart();
      return;
    }

    // Play video
    const playBtn = t.closest('[data-cpc-play]');
    if (playBtn) {
      e.preventDefault();
      e.stopPropagation();
      this._openVideo(playBtn.dataset.videoId);
      return;
    }

    // Quick view button (card)
    if (t.closest('[data-cpc-open-qv]')) {
      e.preventDefault();
      e.stopPropagation();
      this._openQv();
      return;
    }

    // Video → Quick View
    if (t.closest('[data-cpc-video-to-qv]')) {
      e.preventDefault();
      e.stopPropagation();
      this._closeVideo();
      this._openQv();
      return;
    }

    // Close video
    if (t.closest('[data-cpc-close-video]')) {
      e.preventDefault();
      this._closeVideo();
      return;
    }

    // Close QV
    if (t.closest('[data-cpc-close-qv]')) {
      e.preventDefault();
      this._closeQv();
      return;
    }

    // Modal background click to close
    if (t.hasAttribute('data-cpc-modal-bg') || t.classList.contains('cpc__modal-bg')) {
      e.preventDefault();
      this._closeAll();
      return;
    }

    // QV image nav
    if (t.closest('[data-cpc-qv-prev]')) {
      e.preventDefault();
      this._qvImg(-1);
      return;
    }
    if (t.closest('[data-cpc-qv-next]')) {
      e.preventDefault();
      this._qvImg(1);
      return;
    }

    // QV dot nav
    const dot = t.closest('[data-qv-dot]');
    if (dot) {
      e.preventDefault();
      this._qvSelectImg(parseInt(dot.dataset.qvDot));
      return;
    }

    // QV variant
    const qvVBtn = t.closest('.cpc__qv-variant-btn:not([disabled])');
    if (qvVBtn) {
      e.preventDefault();
      this._qvSelectVariant(qvVBtn);
      return;
    }

    // QV quantity
    const qtyBtn = t.closest('[data-cpc-qty]');
    if (qtyBtn) {
      e.preventDefault();
      this._qvChangeQty(parseInt(qtyBtn.dataset.cpcQty));
      return;
    }

    // QV add to cart
    const qvAtc = t.closest('[data-cpc-qv-atc]');
    if (qvAtc && !qvAtc.disabled) {
      e.preventDefault();
      this._qvAddToCart();
      return;
    }
  }

  // ── Variant selection (card) ──────────────────────────────────────────────
  _selectVariant(btn) {
    const id = btn.dataset.variantId;
    const alreadySelected = this._selectedVariantId === id;

    this.querySelectorAll('.cpc__variant-btn').forEach((b) =>
      b.classList.remove('cpc__variant-btn--selected')
    );

    if (alreadySelected) {
      this._selectedVariantId = null;
    } else {
      this._selectedVariantId = id;
      btn.classList.add('cpc__variant-btn--selected');
    }
    this._refreshAtcBtn();
  }

  _refreshAtcBtn() {
    const btn = this.querySelector('[data-cpc-atc]');
    if (!btn) return;
    const hasVariants = this.dataset.hasVariants === 'true';
    const available = this.dataset.productAvailable !== 'false';

    if (!available) {
      btn.disabled = true;
      btn.textContent = 'Sold Out';
      return;
    }
    if (!hasVariants) {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
      return;
    }

    if (this._selectedVariantId) {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    } else {
      btn.disabled = true;
      btn.textContent = 'Select a Size';
    }
  }

  // ── Add to cart ───────────────────────────────────────────────────────────
  _resolveVariantId() {
    const hasVariants = this.dataset.hasVariants === 'true';
    if (!hasVariants) {
      return this.querySelector('[data-variant-id]')?.dataset.variantId;
    }
    return this._selectedVariantId;
  }

  _addToCart(variantId = null, qty = 1) {
    const vid = variantId || this._resolveVariantId();
    if (!vid) return;

    const id = String(vid).includes('/') ? String(vid).split('/').pop() : String(vid);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: qty }] }),
    })
      .then((r) => r.json())
      .then(() => {
        // Notify theme cart drawer / bubble
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch((err) => console.error('[cpc] add-to-cart error:', err));
  }

  // ── Video modal ───────────────────────────────────────────────────────────
  _openVideo(videoId) {
    const modal = this.querySelector('[data-cpc-video-modal]');
    if (!modal) return;

    const iframe = modal.querySelector('[data-cpc-video-iframe]');
    if (iframe && videoId) {
      iframe.src = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&enablejsapi=1&controls=0&loop=1`;
    }

    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  _closeVideo() {
    const modal = this.querySelector('[data-cpc-video-modal]');
    if (!modal) return;
    const iframe = modal.querySelector('[data-cpc-video-iframe]');
    if (iframe) iframe.src = '';
    modal.hidden = true;
    this._restoreScroll();
  }

  // ── Quick view modal ──────────────────────────────────────────────────────
  _openQv() {
    const modal = this.querySelector('[data-cpc-qv-modal]');
    if (!modal) return;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  _closeQv() {
    const modal = this.querySelector('[data-cpc-qv-modal]');
    if (!modal) return;
    modal.hidden = true;
    this._restoreScroll();
  }

  _closeAll() {
    this._closeVideo();
    this._closeQv();
  }

  _restoreScroll() {
    const videoOpen = !this.querySelector('[data-cpc-video-modal]')?.hidden;
    const qvOpen = !this.querySelector('[data-cpc-qv-modal]')?.hidden;
    if (!videoOpen && !qvOpen) document.body.style.overflow = '';
  }

  // ── QV image navigation ───────────────────────────────────────────────────
  _qvImg(dir) {
    const imgs = this.querySelectorAll('[data-qv-img]');
    if (!imgs.length) return;
    let next = this._qvImgIndex + dir;
    if (next < 0) next = imgs.length - 1;
    if (next >= imgs.length) next = 0;
    this._qvSelectImg(next);
  }

  _qvSelectImg(index) {
    this.querySelectorAll('[data-qv-img]').forEach((img, i) => {
      img.hidden = i !== index;
    });
    this.querySelectorAll('[data-qv-dot]').forEach((dot, i) => {
      dot.classList.toggle('cpc__qv-dot--active', i === index);
    });
    this._qvImgIndex = index;
  }

  // ── QV variant selection ──────────────────────────────────────────────────
  _qvSelectVariant(btn) {
    this._qvSelectedVariantId = btn.dataset.qvVariantId;

    this.querySelectorAll('.cpc__qv-variant-btn').forEach((b) =>
      b.classList.remove('cpc__qv-variant-btn--selected')
    );
    btn.classList.add('cpc__qv-variant-btn--selected');

    // Update price if variant-level pricing differs
    const variantPrice = btn.dataset.qvVariantPrice;
    if (variantPrice) {
      const priceEl = this.querySelector('[data-cpc-qv-price]');
      if (priceEl) priceEl.textContent = cpcFormatMoney(parseInt(variantPrice));
    }

    const atcBtn = this.querySelector('[data-cpc-qv-atc]');
    if (atcBtn) {
      atcBtn.disabled = false;
      atcBtn.textContent = 'Add to Cart';
    }

    // Also activate the same size on the card hover panel
    this.querySelectorAll('.cpc__variant-btn').forEach((b) => {
      b.classList.toggle(
        'cpc__variant-btn--selected',
        b.dataset.variantId === this._qvSelectedVariantId
      );
    });
    this._selectedVariantId = this._qvSelectedVariantId;
    this._refreshAtcBtn();
  }

  // ── QV quantity ───────────────────────────────────────────────────────────
  _qvChangeQty(delta) {
    this._qvQty = Math.max(1, Math.min(3, this._qvQty + delta));
    const input = this.querySelector('.cpc__qv-qty-input');
    if (input) input.value = this._qvQty;

    const [minus, plus] = this.querySelectorAll('[data-cpc-qty]');
    if (minus) minus.disabled = this._qvQty <= 1;
    if (plus) plus.disabled = this._qvQty >= 3;
  }

  // ── QV add to cart ────────────────────────────────────────────────────────
  _qvAddToCart() {
    const qvBtns = this.querySelectorAll('[data-qv-variant-id]');
    if (qvBtns.length > 0 && !this._qvSelectedVariantId) return;

    const vid = this._qvSelectedVariantId || qvBtns[0]?.dataset.qvVariantId;
    this._addToCart(vid, this._qvQty);
    this._closeQv();
  }
}

if (!customElements.get('custom-product-card')) {
  customElements.define('custom-product-card', CustomProductCard);
}
